import gql from 'graphql-tag';
import { graphql, ChildProps } from 'react-apollo';
import I18n from 'i18n-js';
import React from 'react';

import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import { RCCheckbox } from '@reverbdotcom/commons/src/rc/forms/RCCheckbox';
import { RCLoadingBars } from '@reverbdotcom/commons/src/rc/RCLoadingBars';
import { RCRadioGroup } from '@reverbdotcom/commons/src/rc/forms/RCRadioGroup';
import { RCSelect } from '@reverbdotcom/commons/src/rc/forms/RCSelect';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';

import ShippingProfileSummaryTable, { MyShippingProfilePricesFragment } from './ShippingProfileSummaryTable';
import { ReverbShippingLabelSelector } from '../discovery/shipping_labels/reverb_shipping_label_selector';
import EditShippingRatesFormContainer from '../shared/edit_shipping_rates_form_container';
import { IShippingRate } from '../shared/edit_shipping_rate_card_manual_prices';
import { FormContext } from '../shared/edit_shipping_rate_card';
import { CoreSellFormMyShippingProfiles, core_apimessages_ShippingLabelPackage } from '../../schemas/rql';

interface IExternalProps {
  existingShippingProfileId: string,
  shippingEnabled: boolean;
  localPickupEnabled: boolean;
  prefersReverbShippingLabelEnabled: boolean;
  allowedToShipCarrierCalculated: boolean;
  carrierCalculatedEnabled: boolean;
  internationalShippingEnabled: boolean;
  currency: string;
  shippingRates: IShippingRate[];
  shipsFromSummary: string;
  shopAddressSummary: string;
  shippingPackage: core_apimessages_ShippingLabelPackage;
}

type IProps = ChildProps<IExternalProps, CoreSellFormMyShippingProfiles.Query>;

export function SellFormShippingOptions({
  data,
  existingShippingProfileId,
  shippingEnabled,
  localPickupEnabled,
  prefersReverbShippingLabelEnabled,
  allowedToShipCarrierCalculated,
  carrierCalculatedEnabled,
  internationalShippingEnabled,
  currency,
  shippingRates,
  shipsFromSummary,
  shopAddressSummary,
  shippingPackage,
}: IProps) {
  const [selectedShippingProfileId, setSelectedShippingProfileId] = React.useState(existingShippingProfileId || '');
  const [listingOffersShipping, setListingOffersShipping] = React.useState(shippingEnabled);
  const [listingOffersLocalPickup, setListingOffersLocalPickup] = React.useState(localPickupEnabled);
  const [prefersReverbShippingLabel, setPrefersReverbShippingLabel] = React.useState(prefersReverbShippingLabelEnabled);
  const [inCarrierCalculatedMode, setIsInCarrierCalculatedMode] = React.useState(allowedToShipCarrierCalculated && carrierCalculatedEnabled);
  const [isShippingInternationally, setIsShippingInternationally] = React.useState(internationalShippingEnabled);

  const { shippingProfiles = [] } = data.me?.shop || {};
  const userHasShippingProfiles = shippingProfiles.length > 0;
  const missingShippingMethod = !listingOffersShipping && !listingOffersLocalPickup;
  const selectedShippingProfile = shippingProfiles.find((sp) => sp.id === selectedShippingProfileId);
  const domesticRegionDisplay = shippingRates.find((sr) => sr.isRequired)?.regionName;

  if (data.loading) {
    return (
      <div className="g-container mb-2">
        <div className="g-col-8 g-col-mobile-12 mb-2 padding-0">
          <SiteModule>
            <div className="d-flex fx-justify-center ptb-6">
              <RCLoadingBars />
            </div>
          </SiteModule>
        </div>
      </div>
    );
  }

  return (
    <div className="g-container mb-2">
      <div className="g-col-8 g-col-mobile-12 mb-2 padding-0">
        <SiteModule title={I18n.t('discovery.dashboard.selling.shippingOptions.header')}>
          {shipsFromSummary && I18n.t('discovery.dashboard.selling.shippingOptions.shipsFrom', { shipsFromSummary })}

          {userHasShippingProfiles && (
            <div className="mtb-2">
              <RCSelect
                id="product_shipping_profile_id"
                name="product[shipping_profile_id]"
                label={I18n.t('discovery.dashboard.selling.shippingOptions.selectProfile')}
                onChange={(e) => setSelectedShippingProfileId(e.target.value)}
                value={selectedShippingProfileId}
              >
                {[
                  <RCSelect.Option
                    key={0}
                    label={I18n.t('discovery.dashboard.selling.shippingOptions.selectProfileNone')}
                    value=""
                  />,
                  ...shippingProfiles.map(({ id, name }) => (
                    <RCSelect.Option
                      key={id}
                      label={name}
                      value={id}
                    />
                  )),
                ]}
              </RCSelect>
            </div>
          )}

          {selectedShippingProfile && <ShippingProfileSummaryTable shippingProfile={selectedShippingProfile} />}

          {!selectedShippingProfile && (
            <>
              <hr className="mtb-3" />

              <p className="size-90 weight-bold mb-1">
                {I18n.t('discovery.dashboard.selling.shippingOptions.deliveryOptionsTitle')}
              </p>

              <div className="mb-1">
                <RCCheckbox
                  id="listingOffersShipping"
                  name="listingOffersShipping"
                  label={I18n.t('discovery.dashboard.selling.shippingOptions.deliveryOptions.shipping')}
                  checked={listingOffersShipping}
                  onChange={() => setListingOffersShipping(!listingOffersShipping)}
                />
                <input
                  hidden
                  readOnly
                  value={listingOffersShipping ? '1' : '0'}
                  name="product[shipping_enabled]"
                  id="product_shipping_enabled"
                  type="hidden"
                />
              </div>

              <RCCheckbox
                id="listingOffersLocalPickup"
                name="listingOffersLocalPickup"
                label={I18n.t('discovery.dashboard.selling.shippingOptions.deliveryOptions.localPickup')}
                checked={listingOffersLocalPickup}
                onChange={() => setListingOffersLocalPickup(!listingOffersLocalPickup)}
              />
              <input
                hidden
                readOnly
                value={listingOffersLocalPickup ? '1' : '0'}
                name="product[shipping_local]"
                id="product_shipping_local"
                type="hidden"
              />

              {missingShippingMethod && (
                <div className="mt-1">
                  <RCAlertBox type="error">
                    {I18n.t('discovery.dashboard.selling.shippingOptions.deliveryOptionsRequired')}
                  </RCAlertBox>
                </div>
              )}

              {listingOffersShipping && (
                <>
                  <hr className="mtb-3" />

                  <RCRadioGroup
                    label={I18n.t('discovery.dashboard.selling.shippingOptions.regionOptions.header')}
                    id="domesticOrInternational"
                    name="domesticOrInternational"
                    value={isShippingInternationally ? 'international' : 'domestic'}
                    onChange={(evt) => setIsShippingInternationally(evt.target.value === 'international')}
                  >
                    <RCRadioGroup.Option
                      label={I18n.t('discovery.dashboard.selling.shippingOptions.regionOptions.domesticAndOthers', { domesticRegionDisplay })}
                      value="international"
                    />
                    <RCRadioGroup.Option
                      label={I18n.t('discovery.dashboard.selling.shippingOptions.regionOptions.domestingOnly', { domesticRegionDisplay })}
                      value="domestic"
                    />
                  </RCRadioGroup>

                  <EditShippingRatesFormContainer
                    formContext={FormContext.LISTING}
                    currency={currency}
                    shippingRates={shippingRates}
                    showInternationalRegions={isShippingInternationally}
                    allowedToShipCarrierCalculated={allowedToShipCarrierCalculated}
                    inCarrierCalculatedMode={inCarrierCalculatedMode}
                    setIsInCarrierCalculatedMode={setIsInCarrierCalculatedMode}
                    shopAddressSummary={shopAddressSummary}
                    shippingPackage={shippingPackage}
                  />

                  {/* Reverb Shipping Label preference toggle hidden with response = NO when carrier calc is active. */}
                  {inCarrierCalculatedMode && (
                    <input
                      hidden
                      readOnly
                      value="0"
                      name="product[prefers_reverb_shipping_label]"
                      id="custom-label-radio"
                      type="hidden"
                    />
                  )}

                  {!inCarrierCalculatedMode && (
                    <>
                      <hr className="mtb-2" />

                      <div className="plr-2">
                        <ReverbShippingLabelSelector
                          prefersReverbShippingLabel={prefersReverbShippingLabel}
                          onChange={setPrefersReverbShippingLabel}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </SiteModule>
      </div>
    </div>
  );
}

export const gearCollectionIndexSearchQuery = gql`
  query Core_SellForm_MyShippingProfiles {
    me {
      ...MyShippingProfilePrices
    }
  },
  ${MyShippingProfilePricesFragment}
`;

const withQuery = graphql<IExternalProps, CoreSellFormMyShippingProfiles.Query>(
  gearCollectionIndexSearchQuery,
);

export default withQuery(SellFormShippingOptions);
